/**
 * Native `*_report_interpretation.pdf` (SHAP force plots, one page per region
 * model) for the browser port — reproduces
 * `src/ads/reporting/interpretation/shap_interpretation.py`'s
 * `gen_report_interpretation` output: `shap.plots.force(..., matplotlib=True,
 * contribution_threshold=0.02, text_rotation=10, out_names=ROI)` on a
 * 20x3in figure, saved with `bbox_inches="tight"` (so page width varies).
 *
 * The page layout is a port of shap 0.50's `plots/_force_matplotlib.py`
 * (`draw_additive_plot` and helpers) on top of a small emulation of the
 * matplotlib 3.9 pieces it relies on: default subplot geometry, text layout
 * of rotated/aligned text incl. the 4pt text bbox padding that shap measures
 * label overlap with, `MaxNLocator(nbins=12)` ticks, `ScalarFormatter`
 * labels, axis-limit `nonsingular` expansion and the tight bounding box.
 * Text uses DejaVu Sans outlines (matplotlib's font) embedded as Type3 fonts
 * (as matplotlib's own PDF backend does), so pages are real vector PDF with
 * extractable text. No dependencies.
 *
 * Input: one QFV row per atlas, keyed by the QFV CSV header (exactly the
 * `*_QFV.csv` files `qfv.ts` writes — use `qfvCsvToFeatureMap` on
 * `buildAllQfvCsvs(...).vascular.qfvCsv` etc. / `buildAspectsPcQfv(...)`).
 * The native pipeline feeds these QFV values to the SHAP models — NOT the mL
 * vectors `interpretation.ts` feeds its ONNX calls. Atlases with no map
 * (e.g. ASPECTSPC when its atlas overlap is unavailable) are skipped.
 */
import { explainAtlas, defaultShapLoader, type InterpretationAtlas, type ShapAssetLoader, type ShapExplanation } from "./shap";
import { DEJAVU_SANS, DEJAVU_SANS_AGG12, DEJAVU_SANS_BOLD, type FontData } from "./shapFont";

export type { InterpretationAtlas } from "./shap";

const ATLAS_ORDER: InterpretationAtlas[] = ["VASCULAR", "LOBE", "ASPECTS", "ASPECTSPC"];

/** Parse a native-format `*_QFV.csv` (header + one row) into the feature map. */
export function qfvCsvToFeatureMap(csv: string): Map<string, number> {
  const lines = csv.trim().split(/\r?\n/);
  const split = (l: string) => {
    const out: string[] = [];
    let cur = "";
    let q = false;
    for (let i = 0; i < l.length; i++) {
      const ch = l[i];
      if (q) {
        if (ch === '"' && l[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') q = false;
        else cur += ch;
      } else if (ch === '"') q = true;
      else if (ch === ",") { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out;
  };
  const header = split(lines[0]);
  const row = split(lines[1] ?? "");
  const m = new Map<string, number>();
  header.forEach((h, i) => {
    if (i === 0 && h === "subject_id") return;
    const v = Number(row[i]);
    m.set(h, Number.isFinite(v) ? v : 0);
  });
  return m;
}

// ---------------------------------------------------------------------------
// Python / numpy formatting helpers
// ---------------------------------------------------------------------------

/** Python `repr(float)`. */
function pyRepr(v: number): string {
  if (Number.isNaN(v)) return "nan";
  if (!Number.isFinite(v)) return v > 0 ? "inf" : "-inf";
  if (v === 0) return Object.is(v, -0) ? "-0.0" : "0.0";
  const a = Math.abs(v);
  if (a < 1e-4 || a >= 1e16) {
    return v.toExponential().replace(/e([+-])(\d)$/, "e$10$2");
  }
  const s = String(v);
  return Number.isInteger(v) ? `${s}.0` : s;
}

/** Python `'%.{d}f' % v` (correctly rounded, ties-to-even on the exact binary value). */
function pyFixed(v: number, d: number): string {
  const s = v.toFixed(d);
  const exact = Math.abs(v).toFixed(Math.min(100, d + 60));
  const tail = exact.slice(exact.indexOf(".") + 1 + d);
  if (/^50*$/.test(tail)) {
    // Exact tie: JS rounds away from zero; Python rounds half to even.
    const lastDigit = Number(s.replace("-", "").replace(".", "").slice(-1));
    if (lastDigit % 2 === 1) {
      const step = 10 ** -d;
      return (v > 0 ? v - step / 2 : v + step / 2).toFixed(d);
    }
  }
  return s;
}

/** Python float `divmod` floor-quotient (also numpy `floor_divide`). */
function pyFloorDiv(x: number, y: number): [number, number] {
  let mod = x % y;
  let div = (x - mod) / y;
  if (mod) {
    if ((y < 0) !== (mod < 0)) { mod += y; div -= 1; }
  } else {
    mod = y < 0 ? -0 : 0;
  }
  let floordiv: number;
  if (div) {
    floordiv = Math.floor(div);
    if (div - floordiv > 0.5) floordiv += 1;
  } else {
    floordiv = x / y < 0 ? -0 : 0;
  }
  return [floordiv, mod];
}

// ---------------------------------------------------------------------------
// Text metrics (matplotlib ft2font, LOAD_NO_HINTING, 72 dpi)
// ---------------------------------------------------------------------------

interface TextMetrics {
  /** `get_width_height` / `get_descent`: advance width, ink height, descent (pt). */
  w: number; h: number; d: number;
  inkXMin: number;
  inkW: number;
}

function kernPts(font: FontData, a: string, b: string, size: number): number {
  const k = font.kern[a + b];
  if (!k) return 0;
  // FT_KERNING_DEFAULT is grid-fitted in ft2font's 8x horizontally
  // oversampled space, then divided back by hinting_factor (8).
  return Math.round((k / font.unitsPerEm) * size * 8) / 8;
}

function glyphOf(font: FontData, ch: string) {
  return font.glyphs[ch] ?? font.glyphs["?"];
}

/** ft2font `set_text(..., LOAD_NO_HINTING)` at 72 dpi (the PDF renderer). */
function measure(text: string, font: FontData, size: number): TextMetrics {
  const sc = size / font.unitsPerEm;
  let pen = 0;
  let x0 = Infinity, x1 = -Infinity, y0 = 0, y1 = 0;
  let prev = "";
  for (const ch of text) {
    if (prev) pen += kernPts(font, prev, ch, size);
    const g = glyphOf(font, ch);
    const [bx0, by0, bx1, by1] = g.b;
    if (!(bx0 === 0 && by0 === 0 && bx1 === 0 && by1 === 0)) {
      x0 = Math.min(x0, pen + bx0 * sc); x1 = Math.max(x1, pen + bx1 * sc);
      y0 = Math.min(y0, by0 * sc); y1 = Math.max(y1, by1 * sc);
    }
    pen += g.a * sc;
    prev = ch;
  }
  if (x0 === Infinity) { x0 = 0; x1 = 0; }
  return { w: pen, h: y1 - y0, d: -y0, inkXMin: x0, inkW: x1 - x0 };
}

/** Hinted Agg metrics at 100 dpi (12pt regular only), converted to pt. */
function measureAgg(text: string): TextMetrics {
  const A = DEJAVU_SANS_AGG12;
  const px = 72 / A.dpi;
  let w = 0, y0 = Infinity, y1 = -Infinity;
  let prev = "";
  for (const ch of text) {
    if (prev) w += A.kern[prev + ch] ?? 0;
    const g = A.glyphs[ch] ?? A.glyphs["?"];
    w += g[0];
    y1 = Math.max(y1, g[1]);
    y0 = Math.min(y0, g[2]);
    prev = ch;
  }
  if (y0 === Infinity) { y0 = 0; y1 = 0; }
  return { w: w * px, h: (y1 - y0) * px, d: -y0 * px, inkXMin: 0, inkW: w * px };
}

type HAlign = "left" | "center" | "right";
type VAlign = "top" | "bottom" | "baseline";

interface TextLayout {
  /** Glyph origin (baseline start) relative to the anchor, pt. */
  ox: number; oy: number;
  /** Layout box AABB relative to anchor (Text.get_window_extent). */
  ext: [number, number, number, number];
  /** Corners of the 4pt-padded text bbox patch relative to anchor. */
  patch: [number, number][];
}

/**
 * `Text._get_layout` (single line, rotation_mode "default") + `_get_textbox`.
 * `math`: mathtext box (ink width, glyph shifted so the ink starts at the
 * box's left edge), used for the shap arrows.
 */
function layoutText(
  text: string, font: FontData, size: number, ha: HAlign, va: VAlign, rotDeg: number,
  opts: { math?: boolean; agg?: boolean } = {},
): TextLayout {
  const m = opts.agg ? measureAgg(text) : measure(text, font, size);
  const lp = opts.agg ? measureAgg("lp") : measure("lp", DEJAVU_SANS, size);
  const w = opts.math ? m.inkW : m.w;
  const h = Math.max(m.h, lp.h);
  const d = Math.max(m.d, lp.d);
  const t = (rotDeg * Math.PI) / 180;
  const c = Math.cos(t), s = Math.sin(t);
  const rot = (x: number, y: number): [number, number] => [x * c - y * s, x * s + y * c];
  const corners = [rot(0, -h), rot(0, 0), rot(w, 0), rot(w, -h)];
  const xs = corners.map((p) => p[0]), ys = corners.map((p) => p[1]);
  const xmin = Math.min(...xs), xmax = Math.max(...xs), ymin = Math.min(...ys), ymax = Math.max(...ys);
  const offx = ha === "center" ? (xmin + xmax) / 2 : ha === "right" ? xmax : xmin;
  const offy = va === "top" ? ymax : va === "baseline" ? ymin + d : ymin;
  const [bx, by] = rot(0, -(h - d));
  const [sx, sy] = rot(opts.math ? -m.inkXMin : 0, 0);
  const pad = 4;
  const patch = [rot(-pad, -h - pad), rot(-pad, pad), rot(w + pad, pad), rot(w + pad, -h - pad)]
    .map(([x, y]) => [x - offx, y - offy] as [number, number]);
  return {
    ox: bx - offx + sx,
    oy: by - offy + sy,
    ext: [xmin - offx, ymin - offy, xmax - offx, ymax - offy],
    patch,
  };
}

// ---------------------------------------------------------------------------
// matplotlib axis emulation
// ---------------------------------------------------------------------------

const FIG_W = 20 * 72;
const FIG_H = 3 * 72;
const AX_L = 0.125 * FIG_W;
const AX_R = 0.9 * FIG_W;
const AX_B = 0.11 * FIG_H;
const AX_T = 0.88 * FIG_H;
const Y_MIN = -0.5;
const Y_MAX = 0.15;

/** `matplotlib.transforms.nonsingular`. */
function nonsingular(vmin: number, vmax: number, expander = 0.05, tiny = 1e-15): [number, number] {
  if (!Number.isFinite(vmin) || !Number.isFinite(vmax)) return [-expander, expander];
  let swapped = false;
  if (vmax < vmin) { [vmin, vmax] = [vmax, vmin]; swapped = true; }
  const maxabs = Math.max(Math.abs(vmin), Math.abs(vmax));
  if (maxabs < (1e6 / tiny) * 2.2250738585072014e-308) {
    vmin = -expander; vmax = expander;
  } else if (vmax - vmin <= maxabs * tiny) {
    if (vmax === 0 && vmin === 0) { vmin = -expander; vmax = expander; }
    else { vmin -= expander * Math.abs(vmin); vmax += expander * Math.abs(vmax); }
  }
  if (swapped) [vmin, vmax] = [vmax, vmin];
  return [vmin, vmax];
}

/** `MaxNLocator(nbins=12, steps=[1, 2, 2.5, 5, 10]).tick_values`. */
function maxNLocatorTicks(vmin0: number, vmax0: number, nbins = 12): number[] {
  const [vmin, vmax] = nonsingular(vmin0, vmax0, 1e-13, 1e-14);
  const dv = Math.abs(vmax - vmin);
  const meanv = (vmax + vmin) / 2;
  const offset = Math.abs(meanv) / dv < 100 ? 0 : Math.sign(meanv) * 10 ** Math.floor(Math.log10(Math.abs(meanv)));
  const scale = 10 ** Math.floor(Math.log10(dv / nbins));
  const _vmin = vmin - offset;
  const _vmax = vmax - offset;
  const steps = [0.1, 0.2, 0.25, 0.5, 1, 2, 2.5, 5, 10, 20].map((v) => v * scale);
  const rawStep = (_vmax - _vmin) / nbins;
  let istep = steps.findIndex((st) => st >= rawStep);
  if (istep < 0) istep = steps.length - 1;
  const closeto = (ms: number, edge: number, step: number) => {
    let tol = 1e-10;
    if (Math.abs(offset) > 0) {
      const digits = Math.log10(Math.abs(offset) / step);
      tol = Math.min(0.4999, Math.max(1e-10, 10 ** (digits - 12)));
    }
    return Math.abs(ms - edge) < tol;
  };
  let ticks: number[] = [];
  for (let k = istep; k >= 0; k--) {
    const step = steps[k];
    const bestVmin = pyFloorDiv(_vmin, step)[0] * step;
    const le = (x: number) => { const [dd, mm] = pyFloorDiv(x, step); return closeto(mm / step, 1, step) ? dd + 1 : dd; };
    const ge = (x: number) => { const [dd, mm] = pyFloorDiv(x, step); return closeto(mm / step, 0, step) ? dd : dd + 1; };
    const low = le(_vmin - bestVmin);
    const high = ge(_vmax - bestVmin);
    ticks = [];
    for (let i = low; i <= high; i++) ticks.push(i * step + bestVmin);
    const nticks = ticks.filter((tk) => tk <= _vmax && tk >= _vmin).length;
    if (nticks >= 2) break;
  }
  return ticks.map((tk) => tk + offset);
}

/** `ScalarFormatter` (useOffset, offset_threshold 4, powerlimits (-5, 6), unicode minus). */
function scalarFormat(locs: number[], vmin: number, vmax: number): { labels: string[]; offsetText: string } {
  const lo = Math.min(vmin, vmax), hi = Math.max(vmin, vmax);
  const inView = locs.filter((l) => lo <= l && l <= hi);
  // _compute_offset
  let offset = 0;
  if (inView.length) {
    const lmin = Math.min(...inView), lmax = Math.max(...inView);
    if (!(lmin === lmax || (lmin <= 0 && 0 <= lmax))) {
      const [absMin, absMax] = [Math.abs(lmin), Math.abs(lmax)].sort((a, b) => a - b);
      const sign = lmin < 0 ? -1 : 1;
      const oomMax = Math.ceil(Math.log10(absMax));
      let oom = oomMax;
      while (pyFloorDiv(absMin, 10 ** oom)[0] === pyFloorDiv(absMax, 10 ** oom)[0]) oom--;
      oom += 1;
      if ((absMax - absMin) / 10 ** oom <= 1e-2) {
        oom = oomMax;
        while (!(absMax - absMin >= 10 ** oom)) oom--;
        oom += 1;
      }
      const n = 4 - 1;
      offset = pyFloorDiv(absMax, 10 ** oom)[0] >= 10 ** n ? sign * pyFloorDiv(absMax, 10 ** oom)[0] * 10 ** oom : 0;
    }
  }
  // _set_order_of_magnitude
  let orderOfMagnitude = 0;
  if (inView.length) {
    let oom: number;
    if (offset) oom = Math.floor(Math.log10(hi - lo));
    else {
      const val = Math.max(...inView.map(Math.abs));
      oom = val === 0 ? 0 : Math.floor(Math.log10(val));
    }
    if (oom <= -5 || oom >= 6) orderOfMagnitude = oom;
  }
  // _set_format
  let ls = locs.length < 2 ? [...locs, vmin, vmax] : locs.slice();
  ls = ls.map((l) => (l - offset) / 10 ** orderOfMagnitude);
  let locRange = Math.max(...ls) - Math.min(...ls);
  if (locRange === 0) locRange = Math.max(...ls.map(Math.abs));
  if (locRange === 0) locRange = 1;
  if (locs.length < 2) ls = ls.slice(0, -2);
  const locRangeOom = Math.floor(Math.log10(locRange));
  let sigfigs = Math.max(0, 3 - locRangeOom);
  const thresh = 1e-3 * 10 ** locRangeOom;
  while (sigfigs >= 0) {
    const maxErr = Math.max(0, ...ls.map((l) => Math.abs(l - Math.round(l * 10 ** sigfigs) / 10 ** sigfigs)));
    if (maxErr < thresh) sigfigs -= 1;
    else break;
  }
  sigfigs += 1;
  const fixMinus = (s: string) => s.replace(/-/g, "−");
  const labels = locs.map((x) => {
    let xp = (x - offset) / 10 ** orderOfMagnitude;
    if (Math.abs(xp) < 1e-8) xp = 0;
    return fixMinus(pyFixed(xp, sigfigs));
  });
  let offsetText = "";
  if (locs.length && (orderOfMagnitude || offset)) {
    let offsetStr = "";
    if (offset) {
      const e = Math.floor(Math.log10(Math.abs(offset)));
      const sgn = Math.round((offset / 10 ** e) * 1e10) / 1e10;
      const significand = sgn % 1 === 0 ? String(Math.trunc(sgn)) : String(Number(sgn.toPrecision(10)));
      offsetStr = e === 0 ? significand : `${significand}e${e}`;
      if (offset > 0) offsetStr = `+${offsetStr}`;
    }
    const sciNotStr = orderOfMagnitude ? `1e${orderOfMagnitude}` : "";
    offsetText = fixMinus(sciNotStr + offsetStr);
  }
  return { labels, offsetText };
}

// ---------------------------------------------------------------------------
// Scene description
// ---------------------------------------------------------------------------

type Pt = [number, number];
/** A coordinate: data x (null = already figure pt), data y / fig y. */
interface Coord { x: number; y: number; fig?: boolean }

type RGB = [number, number, number];

function hex(c: string): RGB {
  const n = parseInt(c.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

interface PolyItem { kind: "poly"; z: number; pts: Coord[]; fill?: RGB; stroke?: RGB; lw?: number; alpha?: number; closed: boolean; cap: 0 | 1 | 2; join: 0 | 1 | 2 }
interface TextItem {
  kind: "text"; z: number; at: Coord; text: string; font: FontData; size: number; color: RGB; alpha: number;
  layout: TextLayout; rot: number; whiteBox: boolean;
}
interface ShadeItem { kind: "shade"; z: number; clip: Coord[]; color: RGB; x0: number; x1: number }
type Item = PolyItem | TextItem | ShadeItem;

interface FeatureRow { pos: number; effect: number; value: string; name: string }

interface Scene {
  items: Item[];
  xlim: [number, number];
}

const RED = "#FF0D57";
const RED_LIGHT = "#FFC3D5";
const BLUE = "#1E88E5";
const BLUE_LIGHT = "#D1E6FA";

class ForcePlot {
  items: Item[] = [];
  xlim: [number, number] = [0, 1];

  setXlim(a: number, b: number) {
    this.xlim = nonsingular(a, b);
  }

  xToFig(x: number): number {
    return AX_L + ((x - this.xlim[0]) / (this.xlim[1] - this.xlim[0])) * (AX_R - AX_L);
  }

  figToX(px: number): number {
    return this.xlim[0] + ((px - AX_L) / (AX_R - AX_L)) * (this.xlim[1] - this.xlim[0]);
  }

  line(xs: number[], ys: number[], color: string, lw: number, alpha: number) {
    this.items.push({
      kind: "poly", z: 2, pts: xs.map((x, i) => ({ x, y: ys[i] })), stroke: hex(color), lw, alpha,
      closed: false, cap: 2, join: 1,
    });
  }

  text(x: number, y: number, s: string, opts: {
    size: number; color?: string; alpha?: number; ha?: HAlign; va?: VAlign; rot?: number; bold?: boolean;
    whiteBox?: boolean; math?: boolean;
  }): TextItem {
    const font = opts.bold ? DEJAVU_SANS_BOLD : DEJAVU_SANS;
    const rot = opts.rot ?? 0;
    const layout = layoutText(s, font, opts.size, opts.ha ?? "left", opts.va ?? "baseline", rot, { math: opts.math });
    const item: TextItem = {
      kind: "text", z: 3, at: { x, y }, text: s, font, size: opts.size, color: hex(opts.color ?? "#000000"),
      alpha: opts.alpha ?? 1, layout, rot, whiteBox: !!opts.whiteBox,
    };
    this.items.push(item);
    return item;
  }
}

/** `draw_bars`. */
function drawBars(p: ForcePlot, out: number, feats: FeatureRow[], type: "positive" | "negative", widthSep: number, widthBar: number) {
  let pre = out;
  feats.forEach((f, index) => {
    let left: number, right: number, indent: number, sepPos: number, colors: [string, string];
    if (type === "positive") {
      left = f.pos; right = pre; pre = left;
      indent = Math.abs(widthSep); sepPos = left; colors = [RED, RED_LIGHT];
    } else {
      left = pre; right = f.pos; pre = right;
      indent = -Math.abs(widthSep); sepPos = right; colors = [BLUE, BLUE_LIGHT];
    }
    let pts: Pt[];
    if (index === 0) {
      pts = type === "positive"
        ? [[left, 0], [right, 0], [right, widthBar], [left, widthBar], [left + indent, widthBar / 2]]
        : [[right, 0], [left, 0], [left, widthBar], [right, widthBar], [right + indent, widthBar / 2]];
    } else {
      pts = [[left, 0], [right, 0], [right + indent * 0.9, widthBar / 2], [right, widthBar], [left, widthBar], [left + indent * 0.9, widthBar / 2]];
    }
    p.items.push({ kind: "poly", z: 1, pts: pts.map(([x, y]) => ({ x, y })), fill: hex(colors[0]), closed: true, cap: 0, join: 0 });
    p.items.push({
      kind: "poly", z: 1, pts: [[sepPos, 0], [sepPos + indent, widthBar / 2], [sepPos, widthBar]].map(([x, y]) => ({ x, y })),
      stroke: hex(colors[1]), lw: 3, closed: false, cap: 0, join: 0,
    });
  });
}

/** `draw_labels`. */
function drawLabels(
  p: ForcePlot, out: number, feats: FeatureRow[], type: "positive" | "negative", offsetText: number,
  totalEffect: number, minPerc: number, textRotation: number,
) {
  let startText = out;
  let pre = out;
  const positive = type === "positive";
  const color = positive ? RED : BLUE;
  const ha: HAlign = positive ? "right" : "left";
  const sign = positive ? 1 : -1;
  if (positive) {
    p.line([pre, pre], [0, -0.18], color, 1, 0.5);
    startText = pre;
  }
  let boxEnd = out;
  for (const f of feats) {
    const contribution = Math.abs(f.pos - pre) / Math.abs(totalEffect);
    if (contribution < minPerc) break;
    const val = f.pos;
    const text = f.value === "" ? f.name : `${f.name} = ${f.value}`;
    const t = p.text(startText - sign * offsetText, -0.15, text, {
      size: 12, color, ha, va: textRotation !== 0 ? "top" : "baseline", rot: textRotation,
    });
    // box_size = bbox patch extents in data coords (with the current xlim),
    // measured, like shap, during an Agg canvas draw (hinted 100-dpi metrics).
    const aggLayout = layoutText(text, DEJAVU_SANS, 12, ha, textRotation !== 0 ? "top" : "baseline", textRotation, { agg: true });
    const ax = p.xToFig(t.at.x);
    const px = aggLayout.patch.map(([x]) => ax + x);
    const boxEndFig = positive ? Math.min(...px) : Math.max(...px);
    const boxEndData = p.figToX(boxEndFig);
    if (sign * boxEndData > sign * val) {
      p.line([val, val], [0, -0.18], color, 1, 0.5);
      startText = val;
      boxEnd = val;
    } else {
      boxEnd = boxEndData - sign * offsetText;
      p.line([val, boxEnd, boxEnd], [0, -0.08, -0.18], color, 1, 0.5);
      startText = boxEnd;
    }
    pre = f.pos;
  }
  const clip: Coord[] = [[out, 0], [pre, 0], [boxEnd, -0.08], [boxEnd, -0.2], [out, -0.2], [out, 0]].map(([x, y]) => ({ x, y }));
  const [lower, upper] = p.xlim;
  if (boxEnd < lower) p.setXlim(boxEnd, upper);
  if (boxEnd > upper) p.setXlim(lower, boxEnd);
  if (out !== boxEnd) {
    p.items.push({ kind: "shade", z: 0, clip, color: hex(color), x0: Math.min(out, boxEnd), x1: Math.max(out, boxEnd) });
  }
}

/** `format_data` + `draw_additive_plot` (identity link). */
function buildScene(e: ShapExplanation, minPerc = 0.02, textRotation = 10): Scene {
  const feats = e.values
    .map((effect, i) => ({ effect, value: pyRepr(e.data[i]), name: e.featureNames[i], pos: 0 }))
    .filter((f) => f.effect !== 0);
  let outValue = e.baseValue;
  for (const v of e.values) outValue += v;
  const baseValue = e.baseValue;

  const neg = feats.filter((f) => f.effect < 0).sort((a, b) => a.effect - b.effect);
  const pos = feats.filter((f) => f.effect >= 0).sort((a, b) => b.effect - a.effect);
  let negVal = outValue;
  for (const f of neg) { negVal += Math.abs(f.effect); f.pos = negVal; }
  const totalNeg = neg.length ? Math.max(...neg.map((f) => f.pos)) - Math.min(...neg.map((f) => f.pos)) : 0;
  let posVal = outValue;
  for (const f of pos) { posVal -= Math.abs(f.effect); f.pos = posVal; }
  const totalPos = pos.length ? Math.max(...pos.map((f) => f.pos)) - Math.min(...pos.map((f) => f.pos)) : 0;

  const offsetText = (Math.abs(totalNeg) + Math.abs(totalPos)) * 0.04;
  const p = new ForcePlot();

  // update_axis_limits
  const padding = Math.max(Math.abs(totalPos) * 0.2, Math.abs(totalNeg) * 0.2);
  const minX = pos.length ? Math.min(Math.min(...pos.map((f) => f.pos)), baseValue) - padding : outValue - padding;
  const maxX = neg.length ? Math.max(Math.max(...neg.map((f) => f.pos)), baseValue) + padding : outValue + padding;
  p.setXlim(minX, maxX);

  const widthBar = 0.1;
  const widthSep = (p.xlim[1] - p.xlim[0]) / 200;
  drawBars(p, outValue, neg, "negative", widthSep, widthBar);
  drawBars(p, outValue, pos, "positive", widthSep, widthBar);

  const totalEffect = Math.abs(totalNeg) + totalPos;
  drawLabels(p, outValue, neg, "negative", offsetText, totalEffect, minPerc, textRotation);
  drawLabels(p, outValue, pos, "positive", offsetText, totalEffect, minPerc, textRotation);

  // draw_higher_lower_element
  p.text(outValue - offsetText, 0.405, "higher", { size: 13, color: RED, ha: "right" });
  p.text(outValue + offsetText, 0.405, "lower", { size: 13, color: BLUE, ha: "left" });
  p.text(outValue, 0.4, "←", { size: 13, color: BLUE, ha: "center", math: true });
  p.text(outValue, 0.425, "→", { size: 13, color: RED, ha: "center", math: true });

  // draw_base_element
  p.line([baseValue, baseValue], [0.13, 0.25], "#F2F2F2", 2, 1);
  p.text(baseValue, 0.33, "base value", { size: 12, alpha: 0.5, ha: "center", whiteBox: true });

  // draw_output_element
  p.line([outValue, outValue], [0, 0.24], "#F2F2F2", 2, 1);
  p.text(outValue, 0.25, pyFixed(outValue, 2), { size: 14, bold: true, ha: "center", whiteBox: true });
  p.text(outValue, 0.33, e.roi, { size: 12, alpha: 0.5, ha: "center", whiteBox: true });

  return { items: p.items, xlim: p.xlim };
}

// ---------------------------------------------------------------------------
// Page rendering (scene -> PDF content stream)
// ---------------------------------------------------------------------------

function num(v: number): string {
  if (Math.abs(v) < 5e-5) return "0";
  return String(Math.round(v * 10000) / 10000);
}

const CODE_OF: Record<string, number> = { "−": 127, "←": 128, "→": 129 };
const CHAR_OF_CODE = new Map<number, string>([[127, "−"], [128, "←"], [129, "→"]]);
for (let c = 32; c <= 126; c++) CHAR_OF_CODE.set(c, String.fromCharCode(c));

function pdfStringBytes(s: string): string {
  let out = "(";
  for (const ch of s) {
    const code = CODE_OF[ch] ?? (ch.charCodeAt(0) >= 32 && ch.charCodeAt(0) <= 126 ? ch.charCodeAt(0) : 63);
    if (code === 40 || code === 41 || code === 92) out += `\\${String.fromCharCode(code)}`;
    else if (code > 126) out += `\\${code.toString(8).padStart(3, "0")}`;
    else out += String.fromCharCode(code);
  }
  return `${out})`;
}

interface PageOut {
  width: number;
  height: number;
  content: string;
  alphas: Set<number>;
  shadings: { name: string; c0: RGB; c1: RGB; y0: number; y1: number }[];
}

function renderPage(e: ShapExplanation, pageIndex: number): PageOut {
  const scene = buildScene(e);
  const [x0, x1] = scene.xlim;
  const X = (x: number) => AX_L + ((x - x0) / (x1 - x0)) * (AX_R - AX_L);
  const Y = (y: number) => AX_B + ((y - Y_MIN) / (Y_MAX - Y_MIN)) * (AX_T - AX_B);
  const P = (c: Coord): Pt => (c.fig ? [c.x, c.y] : [X(c.x), Y(c.y)]);

  // Axis ticks (top), labels, spine.
  const locs = maxNLocatorTicks(x0, x1);
  const { labels, offsetText } = scalarFormat(locs, x0, x1);
  const lo = Math.min(X(x0), X(x1)), hi = Math.max(X(x0), X(x1));
  const tickItems: Item[] = [];
  locs.forEach((loc, i) => {
    const px = X(loc);
    if (px < lo - 1e-10 * (hi - lo) || px > hi + 1e-10 * (hi - lo)) return;
    tickItems.push({
      kind: "poly", z: 1.5, pts: [{ x: px, y: AX_T, fig: true }, { x: px, y: AX_T + 3.5, fig: true }],
      stroke: [0, 0, 0], lw: 0.8, closed: false, cap: 0, join: 0,
    });
    const layout = layoutText(labels[i], DEJAVU_SANS, 10, "center", "bottom", 0);
    tickItems.push({
      kind: "text", z: 1.5, at: { x: px, y: AX_T + 7, fig: true }, text: labels[i], font: DEJAVU_SANS, size: 10,
      color: [0, 0, 0], alpha: 1, layout, rot: 0, whiteBox: false,
    });
  });
  if (offsetText) {
    const layout = layoutText(offsetText, DEJAVU_SANS, 10, "right", "top", 0);
    tickItems.push({
      kind: "text", z: 1.5, at: { x: AX_R, y: AX_B - 3, fig: true }, text: offsetText, font: DEJAVU_SANS, size: 10,
      color: [0, 0, 0], alpha: 1, layout, rot: 0, whiteBox: false,
    });
  }
  const spine: Item = {
    kind: "poly", z: 2.5, pts: [{ x: AX_L, y: AX_T, fig: true }, { x: AX_R, y: AX_T, fig: true }],
    stroke: [0, 0, 0], lw: 0.8, closed: false, cap: 2, join: 0,
  };
  const items = [...scene.items, ...tickItems, spine]
    .map((it, i) => ({ it, i }))
    .sort((a, b) => a.it.z - b.it.z || a.i - b.i)
    .map((a) => a.it);

  // Tight bbox: axes box, texts (layout extents), unclipped lines, images.
  let bx0 = AX_L, by0 = AX_B, bx1 = AX_R, by1 = AX_T;
  const grow = (x: number, y: number) => { bx0 = Math.min(bx0, x); bx1 = Math.max(bx1, x); by0 = Math.min(by0, y); by1 = Math.max(by1, y); };
  for (const it of items) {
    if (it.kind === "text") {
      const [ax, ay] = P(it.at);
      grow(ax + it.layout.ext[0], ay + it.layout.ext[1]);
      grow(ax + it.layout.ext[2], ay + it.layout.ext[3]);
    } else if (it.kind === "poly" && it.z === 2) {
      for (const c of it.pts) grow(...P(c));
    } else if (it.kind === "shade") {
      grow(X(it.x0), Y(0)); grow(X(it.x1), Y(-0.31));
    }
  }
  const pad = 7.2;
  const ox = bx0 - pad, oy = by0 - pad;
  const width = bx1 - bx0 + 2 * pad;
  const height = by1 - by0 + 2 * pad;

  const alphas = new Set<number>();
  const shadings: PageOut["shadings"] = [];
  const ops: string[] = [];
  ops.push(`1 1 1 rg 0 0 ${num(width)} ${num(height)} re f`);
  ops.push(`1 0 0 1 ${num(-ox)} ${num(-oy)} cm`);
  const gs = (a: number) => {
    if (a >= 1) return "";
    const k = Math.round(a * 100);
    alphas.add(k);
    return `/GA${k} gs `;
  };
  const rgb = (c: RGB) => c.map((v) => num(v)).join(" ");
  const path = (pts: Pt[], closed: boolean) =>
    pts.map(([x, y], i) => `${num(x)} ${num(y)} ${i === 0 ? "m" : "l"}`).join(" ") + (closed ? " h" : "");
  const axesClip = `${num(AX_L)} ${num(AX_B)} ${num(AX_R - AX_L)} ${num(AX_T - AX_B)} re W n`;

  for (const it of items) {
    if (it.kind === "shade") {
      // imshow gradient: colour at y=0 fading (alpha 0.3, over white) to
      // white at the colormap's vmax (row ~24.5 of 50 over [0, -0.31]).
      const name = `Sh${pageIndex}_${shadings.length}`;
      const c0 = it.color.map((v) => 0.7 + 0.3 * v) as RGB;
      shadings.push({ name, c0, c1: [1, 1, 1], y0: Y(-0.31 * 0.5 / 50), y1: Y(-0.31 * 25.02 / 50) });
      ops.push(`q ${axesClip} ${path(it.clip.map(P), true)} W n /${name} sh Q`);
    } else if (it.kind === "poly") {
      const pts = it.pts.map(P);
      const clip = it.z === 1 ? `${axesClip} ` : "";
      if (it.fill) {
        ops.push(`q ${clip}${gs(it.alpha ?? 1)}${rgb(it.fill)} rg ${path(pts, true)} f Q`);
      } else if (it.stroke) {
        ops.push(`q ${clip}${gs(it.alpha ?? 1)}${rgb(it.stroke)} RG ${num(it.lw ?? 1)} w ${it.cap} J ${it.join} j ${path(pts, it.closed)} S Q`);
      }
    } else {
      const [ax, ay] = P(it.at);
      if (it.whiteBox) {
        const corners = it.layout.patch.map(([x, y]) => [ax + x, ay + y] as Pt);
        ops.push(`q 1 1 1 rg 1 1 1 RG 1 w 0 j ${path(corners, true)} B Q`);
      }
      const t = (it.rot * Math.PI) / 180;
      const c = Math.cos(t), s = Math.sin(t);
      const gx = ax + it.layout.ox, gy = ay + it.layout.oy;
      const fontName = it.font === DEJAVU_SANS_BOLD ? "F2" : "F1";
      // Kerning as TJ adjustments (thousandths of text-space units).
      const chars = [...it.text];
      const parts: string[] = [];
      let run = "";
      chars.forEach((ch, i) => {
        if (i > 0) {
          const k = kernPts(it.font, chars[i - 1], ch, it.size);
          if (k !== 0) {
            parts.push(pdfStringBytes(run));
            parts.push(num((-k / it.size) * 1000));
            run = "";
          }
        }
        run += ch;
      });
      parts.push(pdfStringBytes(run));
      ops.push(
        `q ${gs(it.alpha)}BT /${fontName} ${num(it.size)} Tf ${rgb(it.color)} rg ` +
        `${num(c)} ${num(s)} ${num(-s)} ${num(c)} ${num(gx)} ${num(gy)} Tm [${parts.join(" ")}] TJ ET Q`,
      );
    }
  }
  return { width, height, content: ops.join("\n"), alphas, shadings };
}

// ---------------------------------------------------------------------------
// Minimal PDF writer
// ---------------------------------------------------------------------------

function glyphProc(font: FontData, ch: string): string {
  const g = glyphOf(font, ch);
  const [bx0, by0, bx1, by1] = g.b;
  const ops: string[] = [`${g.a} 0 ${bx0} ${by0} ${bx1} ${by1} d1`];
  const tok = g.p.match(/[MLQZ]|-?[\d.]+(?:e-?\d+)?/g) ?? [];
  let i = 0;
  let cx = 0, cy = 0;
  const n = () => Number(tok[i++]);
  while (i < tok.length) {
    const op = tok[i++];
    if (op === "M") { cx = n(); cy = n(); ops.push(`${num(cx)} ${num(cy)} m`); }
    else if (op === "L") { cx = n(); cy = n(); ops.push(`${num(cx)} ${num(cy)} l`); }
    else if (op === "Q") {
      const qx = n(), qy = n(), x = n(), y = n();
      const c1x = cx + (2 / 3) * (qx - cx), c1y = cy + (2 / 3) * (qy - cy);
      const c2x = x + (2 / 3) * (qx - x), c2y = y + (2 / 3) * (qy - y);
      ops.push(`${num(c1x)} ${num(c1y)} ${num(c2x)} ${num(c2y)} ${num(x)} ${num(y)} c`);
      cx = x; cy = y;
    } else if (op === "Z") ops.push("h");
  }
  if (g.p) ops.push("f");
  return ops.join("\n");
}

async function deflate(data: Uint8Array): Promise<Uint8Array | null> {
  const CS = (globalThis as { CompressionStream?: typeof CompressionStream }).CompressionStream;
  if (!CS) return null;
  try {
    const stream = new Blob([data as BlobPart]).stream().pipeThrough(new CS("deflate"));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    return null;
  }
}

class PdfWriter {
  private objects: (Uint8Array | null)[] = [];
  private enc = new TextEncoder();

  reserve(): number {
    this.objects.push(null);
    return this.objects.length;
  }

  set(id: number, body: string | Uint8Array) {
    this.objects[id - 1] = typeof body === "string" ? this.enc.encode(body) : body;
  }

  add(body: string): number {
    const id = this.reserve();
    this.set(id, body);
    return id;
  }

  async addStream(dict: string, content: string): Promise<number> {
    const raw = this.enc.encode(content);
    const z = await deflate(raw);
    const data = z ?? raw;
    const head = this.enc.encode(`<< ${dict} /Length ${data.length}${z ? " /Filter /FlateDecode" : ""} >>\nstream\n`);
    const tail = this.enc.encode("\nendstream");
    const body = new Uint8Array(head.length + data.length + tail.length);
    body.set(head, 0);
    body.set(data, head.length);
    body.set(tail, head.length + data.length);
    const id = this.reserve();
    this.set(id, body);
    return id;
  }

  finish(rootId: number, infoId: number): Uint8Array {
    const chunks: Uint8Array[] = [];
    let len = 0;
    const push = (b: Uint8Array) => { chunks.push(b); len += b.length; };
    push(this.enc.encode("%PDF-1.4\n%\xe2\xe3\xcf\xd3\n"));
    const offsets: number[] = [];
    this.objects.forEach((o, i) => {
      offsets.push(len);
      push(this.enc.encode(`${i + 1} 0 obj\n`));
      push(o ?? this.enc.encode("null"));
      push(this.enc.encode("\nendobj\n"));
    });
    const xref = len;
    let x = `xref\n0 ${this.objects.length + 1}\n0000000000 65535 f \n`;
    for (const off of offsets) x += `${String(off).padStart(10, "0")} 00000 n \n`;
    x += `trailer\n<< /Size ${this.objects.length + 1} /Root ${rootId} 0 R /Info ${infoId} 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
    push(this.enc.encode(x));
    const out = new Uint8Array(len);
    let o = 0;
    for (const c of chunks) { out.set(c, o); o += c.length; }
    return out;
  }
}

function pdfText(s: string): string {
  return `(${s.replace(/[\\()]/g, (m) => `\\${m}`).replace(/[^\x20-\x7e]/g, "?")})`;
}

async function addType3Font(w: PdfWriter, font: FontData, name: string): Promise<number> {
  const codes = [...CHAR_OF_CODE.keys()].sort((a, b) => a - b);
  const procs: string[] = [];
  for (const code of codes) {
    const id = await w.addStream("", glyphProc(font, CHAR_OF_CODE.get(code)!));
    procs.push(`/g${code} ${id} 0 R`);
  }
  let cmap = "/CIDInit /ProcSet findresource begin 12 dict begin begincmap\n" +
    "/CIDSystemInfo << /Registry (Adobe) /Ordering (UCS) /Supplement 0 >> def\n" +
    "/CMapName /Adobe-Identity-UCS def /CMapType 2 def\n1 begincodespacerange <00> <FF> endcodespacerange\n" +
    `${codes.length} beginbfchar\n`;
  for (const code of codes) {
    const u = CHAR_OF_CODE.get(code)!.charCodeAt(0);
    cmap += `<${code.toString(16).padStart(2, "0")}> <${u.toString(16).padStart(4, "0")}>\n`;
  }
  cmap += "endbfchar\nendcmap CMapName currentdict /CMap defineresource pop end end";
  const toUnicode = await w.addStream("", cmap);
  let fx0 = 0, fy0 = 0, fx1 = 0, fy1 = 0;
  for (const g of Object.values(font.glyphs)) {
    fx0 = Math.min(fx0, g.b[0]); fy0 = Math.min(fy0, g.b[1]); fx1 = Math.max(fx1, g.b[2]); fy1 = Math.max(fy1, g.b[3]);
  }
  const first = codes[0], last = codes[codes.length - 1];
  const widths: number[] = [];
  for (let c = first; c <= last; c++) widths.push(CHAR_OF_CODE.has(c) ? glyphOf(font, CHAR_OF_CODE.get(c)!).a : 0);
  const s = 1 / font.unitsPerEm;
  return w.add(
    `<< /Type /Font /Subtype /Type3 /Name /${name} /FontBBox [${fx0} ${fy0} ${fx1} ${fy1}] ` +
    `/FontMatrix [${s} 0 0 ${s} 0 0] /CharProcs << ${procs.join(" ")} >> ` +
    `/Encoding << /Type /Encoding /Differences [${first} ${codes.map((c) => `/g${c}`).join(" ")}] >> ` +
    `/FirstChar ${first} /LastChar ${last} /Widths [${widths.join(" ")}] /Resources << >> /ToUnicode ${toUnicode} 0 R >>`,
  );
}

/** One multi-page force-plot PDF (one page per explanation, native order). */
export async function renderInterpretationPdf(explanations: ShapExplanation[], title: string): Promise<Uint8Array> {
  const w = new PdfWriter();
  const catalogId = w.reserve();
  const pagesId = w.reserve();
  const f1 = await addType3Font(w, DEJAVU_SANS, "F1");
  const f2 = await addType3Font(w, DEJAVU_SANS_BOLD, "F2");
  const pageIds: number[] = [];
  for (let i = 0; i < explanations.length; i++) {
    const pg = renderPage(explanations[i], i);
    const contentId = await w.addStream("", pg.content);
    const ext = [...pg.alphas].map((k) => `/GA${k} << /Type /ExtGState /ca ${k / 100} /CA ${k / 100} >>`).join(" ");
    const sh = pg.shadings.map((s) =>
      `/${s.name} << /ShadingType 2 /ColorSpace /DeviceRGB /Coords [0 ${num(s.y0)} 0 ${num(s.y1)}] ` +
      `/Function << /FunctionType 2 /Domain [0 1] /C0 [${s.c0.map(num).join(" ")}] /C1 [${s.c1.map(num).join(" ")}] /N 1 >> ` +
      `/Extend [true true] >>`).join(" ");
    pageIds.push(w.add(
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${num(pg.width)} ${num(pg.height)}] ` +
      `/Resources << /Font << /F1 ${f1} 0 R /F2 ${f2} 0 R >> /ExtGState << ${ext} >> /Shading << ${sh} >> >> ` +
      `/Contents ${contentId} 0 R >>`,
    ));
  }
  w.set(pagesId, `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`);
  w.set(catalogId, `<< /Type /Catalog /Pages ${pagesId} 0 R >>`);
  const infoId = w.add(`<< /Title ${pdfText(title)} /Producer (OpenADS desk - shapPdf.ts) >>`);
  return w.finish(catalogId, infoId);
}

/**
 * Native `{subjectId}_{ATLAS}_report_interpretation.pdf` for each atlas whose
 * QFV feature map is provided (one page per region model, native order;
 * ASPECTSPC's two LogisticRegression pages render as the native empty plot).
 * An atlas that fails (e.g. model files missing) is skipped with a warning,
 * like native `gen_report_interpretation`.
 */
export async function buildInterpretationPdfs(input: {
  features: Partial<Record<InterpretationAtlas, Map<string, number>>>;
  subjectId: string;
  /** Override for loading `public/models/aa-shap/*` (tests / Node). */
  loader?: ShapAssetLoader;
}): Promise<Partial<Record<InterpretationAtlas, Blob>>> {
  const out: Partial<Record<InterpretationAtlas, Blob>> = {};
  for (const atlas of ATLAS_ORDER) {
    const features = input.features[atlas];
    if (!features || features.size === 0) continue;
    try {
      const explanations = await explainAtlas(atlas, features, input.loader ?? defaultShapLoader);
      if (!explanations.length) continue;
      const bytes = await renderInterpretationPdf(explanations, `${input.subjectId}_${atlas}_report_interpretation`);
      out[atlas] = new Blob([bytes as BlobPart], { type: "application/pdf" });
    } catch (err) {
      console.warn(`[report] ${atlas} interpretation PDF skipped: ${(err as Error).message}`);
    }
  }
  return out;
}
